import React, { useRef, useState, useEffect } from 'react';

export function ScaledToFit({
  designWidth = 1400,
  maxScale = 1,
  children,
  className = '',
  enabled = true,
}: {
  designWidth?: number;
  maxScale?: number;
  children: React.ReactNode;
  className?: string;
  enabled?: boolean;
}) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [outerHeight, setOuterHeight] = useState<number | undefined>(undefined);

  // Recompute scale when parent resizes
  useEffect(() => {
    if (!enabled || !outerRef.current) {
      return;
    }
    const ro = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      const s = Math.min(width / designWidth, maxScale);
      setScale(s);
    });
    ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [designWidth, maxScale, enabled]);

  // Reserve correct height (transform doesn't affect layout)
  useEffect(() => {
    if (!enabled || !innerRef.current) {
      return;
    }
    const updateHeight = () => {
      // offsetHeight is unscaled height; multiply by scale to reserve space
      setOuterHeight((innerRef.current?.offsetHeight || 0) * scale);
    };
    updateHeight();

    // If inner content changes height dynamically, observe it
    const ro = new ResizeObserver(updateHeight);
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [scale, enabled]);

  return (
    <div
      ref={outerRef}
      className={`${className}`}
      style={{
        position: 'relative',
        height: enabled ? outerHeight : undefined, // reserve scaled height only when enabled
      }}
    >
      <div
        ref={innerRef}
        style={{
          width: enabled ? designWidth : 'auto',
          transform: enabled ? `scale(${scale})` : 'none',
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}
